import { ScheduleType } from "src/components/schedule/types";

/**
 * Intentionally duplicated from @hightouch/lib/match-booster/types.ts to avoid
 * excessive backend imports. That module type-imports the backend query graph,
 * and the app compiles it under stricter settings, so importing it adds
 * thousands of typecheck errors even though the runtime bundle is unaffected.
 */
export const MATCH_BOOSTER_REGIONS = ["us", "eu"] as const;
export type MatchBoosterRegion = (typeof MATCH_BOOSTER_REGIONS)[number];

export function isMatchBoosterRegion(
  value: unknown,
): value is MatchBoosterRegion {
  return (
    typeof value === "string" &&
    MATCH_BOOSTER_REGIONS.includes(value as MatchBoosterRegion)
  );
}

export function getEnabledMatchBoosterRegions(
  settings: unknown,
): MatchBoosterRegion[] {
  const regions =
    settings && typeof settings === "object"
      ? (settings as { regions?: unknown }).regions
      : undefined;
  if (!regions || typeof regions !== "object" || Array.isArray(regions)) {
    return ["us"];
  }

  const enabled = MATCH_BOOSTER_REGIONS.filter((region) =>
    Object.hasOwn(regions, region),
  );
  return enabled.length > 0 ? enabled : ["us"];
}

export function getEnabledMatchBoosterRegionsForModel(model: {
  matchboosting_enabled?: boolean | null;
  matchbooster_settings?: unknown;
  parent?: {
    matchboosting_enabled?: boolean | null;
    matchbooster_settings?: unknown;
  } | null;
}): MatchBoosterRegion[] {
  const settingsModel = model.parent ?? model;
  if (!settingsModel.matchboosting_enabled) {
    return [];
  }
  return getEnabledMatchBoosterRegions(settingsModel.matchbooster_settings);
}

export function getMatchBoosterRegionSettings(
  settings: unknown,
  region: MatchBoosterRegion,
): Record<string, unknown> {
  if (!settings || typeof settings !== "object" || Array.isArray(settings)) {
    return {};
  }

  const rawSettings = settings as Record<string, unknown>;
  const regions = rawSettings.regions;
  if (regions && typeof regions === "object" && !Array.isArray(regions)) {
    const regionSettings = (regions as Record<string, unknown>)[region];
    return regionSettings &&
      typeof regionSettings === "object" &&
      !Array.isArray(regionSettings)
      ? (regionSettings as Record<string, unknown>)
      : {};
  }

  return region === "us" ? rawSettings : {};
}

export function containsMatchBoosterColumn(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some(containsMatchBoosterColumn);
  }
  if (!value || typeof value !== "object") {
    return false;
  }
  if ((value as { type?: unknown }).type === "boosted") {
    return true;
  }
  return Object.values(value).some(containsMatchBoosterColumn);
}

/**
 * Not shared with the backend's isMatchBoosterQuery, which reads boostedColumns
 * off a compiled Query. No Query exists at form-authoring time, so that helper
 * would always return false here and silently disable the region gating.
 */
export function syncUsesMatchBooster({
  config,
  schedule,
}: {
  config: unknown;
  schedule?: { type?: unknown } | null;
}): boolean {
  return (
    schedule?.type === ScheduleType.MATCH_BOOSTER ||
    containsMatchBoosterColumn(config)
  );
}

/**
 * A region can only be reassigned before the sync's first run. A region
 * change does not force a resync, so boosted columns are skipped by diffing
 * and rows already at the destination keep serving the old region's
 * enrichment. That risk exists even for a saved region the model no longer
 * enables, since the rows a run already delivered do not move.
 *
 * The single-region-model case (enabled length 1, saved region still among
 * them) is excluded here even though it has run, because the picker is
 * already disabled by the unrelated single-region rule in
 * `MatchBoosterRegionSelect` and should show that message, not lock copy.
 */
export function isMatchBoosterRegionPinned({
  enabledRegions,
  savedRegion,
  syncRan,
}: {
  enabledRegions: MatchBoosterRegion[];
  savedRegion: MatchBoosterRegion | null | undefined;
  syncRan: boolean;
}): boolean {
  return (
    isMatchBoosterRegion(savedRegion) &&
    syncRan &&
    (enabledRegions.length > 1 || !enabledRegions.includes(savedRegion))
  );
}

/**
 * Region that a sync's boosted reads (match rate stats, enrichment previews)
 * should use. A sync being created has nothing persisted yet, so the region
 * picked in the form has to win over the model's first enabled region.
 */
export function resolveMatchBoosterRegion({
  selectedRegion,
  savedRegion,
  enabledRegions,
}: {
  selectedRegion: MatchBoosterRegion | null | undefined;
  savedRegion: unknown;
  enabledRegions: MatchBoosterRegion[];
}): MatchBoosterRegion {
  if (selectedRegion) {
    return selectedRegion;
  }
  if (isMatchBoosterRegion(savedRegion)) {
    return savedRegion;
  }
  return enabledRegions[0] ?? "us";
}

/** Region value to write on an existing sync, or undefined to leave it unchanged. */
export function getMatchBoosterRegionToPersistForSync(args: {
  enabledRegions: MatchBoosterRegion[];
  selectedRegion: MatchBoosterRegion | undefined;
  savedRegion?: MatchBoosterRegion | null;
  syncRan: boolean;
}): MatchBoosterRegion | undefined {
  if (
    isMatchBoosterRegionPinned({
      enabledRegions: args.enabledRegions,
      savedRegion: args.savedRegion,
      syncRan: args.syncRan,
    })
  ) {
    return undefined;
  }
  return getMatchBoosterRegionToPersist(args);
}

export function getMatchBoosterRegionToPersist({
  enabledRegions,
  selectedRegion,
  savedRegion,
}: {
  enabledRegions: MatchBoosterRegion[];
  selectedRegion: MatchBoosterRegion | undefined;
  savedRegion?: MatchBoosterRegion | null;
}): MatchBoosterRegion | undefined {
  if (enabledRegions.length > 1 || savedRegion) {
    return selectedRegion;
  }
  return undefined;
}

/** Region value to write on a template create/update, or undefined to leave unchanged. */
export function getMatchBoosterRegionForTemplateMutation({
  usesMatchBooster,
  enabledRegions,
  selectedRegion,
  savedRegion,
}: {
  usesMatchBooster: boolean;
  enabledRegions: MatchBoosterRegion[];
  selectedRegion: MatchBoosterRegion | null | undefined;
  savedRegion?: MatchBoosterRegion | null;
}): MatchBoosterRegion | null | undefined {
  if (!usesMatchBooster) {
    return savedRegion ? null : undefined;
  }

  const selected =
    selectedRegion ??
    (enabledRegions.length === 1 ? enabledRegions[0] : undefined);

  return getMatchBoosterRegionToPersist({
    enabledRegions,
    selectedRegion: selected,
    savedRegion,
  });
}

/**
 * Household targeting needs both the model's opt-in and a non-EU region.
 * The EU graph is person-only, so household stays disabled there even when
 * the model itself has `useHouseholdTargeting` on.
 */
export function canSelectHouseholdTargeting({
  region,
  useHouseholdTargeting,
}: {
  region: MatchBoosterRegion | null | undefined;
  useHouseholdTargeting: boolean;
}): boolean {
  return region !== "eu" && useHouseholdTargeting === true;
}

export const EU_HOUSEHOLD_TARGETING_MESSAGE =
  "Household targeting is not available in Europe.";

/** True when a save would persist household targeting on an EU region. */
export function isEuHouseholdTargetingCombo({
  region,
  targetingLevel,
}: {
  region: MatchBoosterRegion | null | undefined;
  targetingLevel: unknown;
}): boolean {
  return region === "eu" && targetingLevel === "household";
}

export function requiresExplicitMatchBoosterRegion({
  usesMatchBooster,
  enabledRegions,
  selectedRegion,
}: {
  usesMatchBooster: boolean;
  enabledRegions: MatchBoosterRegion[];
  selectedRegion: MatchBoosterRegion | null | undefined;
}): boolean {
  return (
    usesMatchBooster &&
    enabledRegions.length > 1 &&
    !isMatchBoosterRegion(selectedRegion)
  );
}
